import { useMemo, useState, useLayoutEffect } from 'react';
import type { RefObject } from 'react';
import type { QuoteProductDTO } from '@setvi/shared/services/react-query';

const OPTION_ROW_HEIGHT = 118;
const OPTION_ROW_HEIGHT_WITH_BADGE = 122;
const OPTION_ROW_HEIGHT_WITH_NOTE = 140;
const OPTION_ROW_HEIGHT_WITH_BADGE_AND_NOTE = 148;
const OVERSCAN = 2;

interface UseOptionVirtualizerOptions {
  options: QuoteProductDTO[];
  scrollContainerRef: RefObject<HTMLDivElement>;
  productStartOffset: number;
  productHeaderHeight: number;
  enabled: boolean;
}

interface VirtualItem {
  index: number;
  start: number;
  size: number;
  key: string;
}

/**
 * Get the height of an option row based on badge (labelType) and note
 */
const getOptionRowHeight = (option: QuoteProductDTO): number => {
  const hasNote = !!option?.note?.trim();
  const hasBadge = !!option?.labelType;
  if (hasNote)
    return hasBadge
      ? OPTION_ROW_HEIGHT_WITH_BADGE_AND_NOTE
      : OPTION_ROW_HEIGHT_WITH_NOTE;
  return hasBadge ? OPTION_ROW_HEIGHT_WITH_BADGE : OPTION_ROW_HEIGHT;
};

/**
 * Virtualizes option rows within a ProductRow component.
 *
 * This hook observes the parent scroll container and calculates which
 * option rows should be rendered based on their absolute position
 * within the scroll container.
 *
 * Supports variable height rows - options with notes are taller.
 *
 * @param options - Array of child products (options)
 * @param scrollContainerRef - Ref to the main scroll container
 * @param productStartOffset - Where the parent product starts in the scroll container
 * @param productHeaderHeight - Height of the product header (before options)
 * @param enabled - Whether virtualization is enabled
 */
export const useOptionVirtualizer = ({
  options,
  scrollContainerRef,
  productStartOffset,
  productHeaderHeight,
  enabled
}: UseOptionVirtualizerOptions) => {
  const [scrollTop, setScrollTop] = useState(0);
  const [containerHeight, setContainerHeight] = useState(0);

  // Calculate total size and item positions with variable heights
  const { totalSize, itemPositions, itemSizes } = useMemo(() => {
    if (!enabled || options.length === 0) {
      return {
        totalSize: 0,
        itemPositions: [] as number[],
        itemSizes: [] as number[]
      };
    }

    const positions: number[] = [];
    const sizes: number[] = [];
    let offset = 0;

    for (let i = 0; i < options.length; i += 1) {
      positions.push(offset);
      const height = getOptionRowHeight(options[i]);
      sizes.push(height);
      offset += height;
    }

    return {
      totalSize: offset,
      itemPositions: positions,
      itemSizes: sizes
    };
  }, [options, enabled]);

  // Listen to scroll events
  useLayoutEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || !enabled) return undefined;

    const handleScroll = () => {
      setScrollTop(container.scrollTop);
      setContainerHeight(container.clientHeight);
    };

    // Initial values
    handleScroll();

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [scrollContainerRef, enabled]);

  // Calculate visible items based on scroll position
  const virtualItems = useMemo((): VirtualItem[] => {
    if (!enabled || options.length === 0 || containerHeight === 0) {
      return [];
    }

    // Options start after the product header
    const optionsTop = productStartOffset + productHeaderHeight;
    const viewportTop = scrollTop;
    const viewportBottom = scrollTop + containerHeight;

    const items: VirtualItem[] = [];

    for (let i = 0; i < options.length; i += 1) {
      const itemStart = itemPositions[i];
      const itemSize = itemSizes[i];
      const itemEnd = itemStart + itemSize;

      // Item's absolute position in the scroll container
      const absoluteStart = optionsTop + itemStart;
      const absoluteEnd = optionsTop + itemEnd;

      // Check if item is in or near viewport (with overscan)
      const overscanPx = OVERSCAN * OPTION_ROW_HEIGHT;
      const isVisible =
        absoluteEnd >= viewportTop - overscanPx &&
        absoluteStart <= viewportBottom + overscanPx;

      if (isVisible) {
        items.push({
          index: i,
          start: itemStart,
          size: itemSize,
          key: options[i].id || options[i]._id || String(i)
        });
      }
    }

    return items;
  }, [
    enabled,
    options,
    containerHeight,
    scrollTop,
    productStartOffset,
    productHeaderHeight,
    itemPositions,
    itemSizes
  ]);

  return {
    virtualItems,
    totalSize
  };
};
